// Storefront v2 content pages (planning/022-storefront-v2 stage 11). Progressive enhancement only:
// every form here also works without JS.

/**
 * <am-pair-form>: Room "add the pair" button (guide §04 Room). Adds both works in one /cart/add.js
 * call, then announces it so am-chrome.js refreshes and opens the v2 cart drawer.
 */
class AmPairForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (e) => this.add(e));
  }

  async add(e) {
    e.preventDefault();
    const btn = this.querySelector('[data-am-pair-add]');
    const error = this.querySelector('[data-am-pair-error]');
    error?.setAttribute('hidden', '');
    btn?.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.form),
      });
      if (!res.ok) throw new Error(`add ${res.status}`);
      document.dispatchEvent(new CustomEvent('am:cart-added', { detail: { pair: true } }));
    } catch {
      error?.removeAttribute('hidden');
    } finally {
      btn?.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('am-pair-form')) customElements.define('am-pair-form', AmPairForm);

/**
 * <am-contact-form>: Contact topic chips (guide §04 Contact). The chips are radios, so the topic
 * posts without JS; the script picks the chip named by ?topic= and shows the order-number field
 * and the damaged-print callout only while the order topic is chosen (both stay visible without JS).
 */
class AmContactForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (e) => {
      if (e.target.name === 'contact[topic]') this.sync();
    });
    this.preselect();
    this.sync();
  }

  // ?topic=<id> (For artists links here with ?topic=artist) picks that chip on arrival.
  preselect() {
    const wanted = new URLSearchParams(window.location.search).get('topic');
    if (!wanted) return;
    const chip = this.querySelector(`#am-contact-topic-${CSS.escape(wanted)}`);
    if (chip?.name === 'contact[topic]') chip.checked = true;
  }

  sync() {
    const topic = this.querySelector('input[name="contact[topic]"]:checked');
    const isOrder = topic?.hasAttribute('data-am-topic-order') ?? false;
    for (const el of this.querySelectorAll('[data-am-order-only]')) {
      el.hidden = !isOrder;
      for (const input of el.querySelectorAll('input')) input.disabled = !isOrder;
    }
  }
}
if (!customElements.get('am-contact-form')) customElements.define('am-contact-form', AmContactForm);

/**
 * <am-scroll-spy>: Help's side nav marks the part being read (aria-current), from an observer on
 * a band under the sticky header. No scroll listener.
 */
class AmScrollSpy extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;
    const links = new Map([...this.querySelectorAll('a[href^="#"]')].map((a) => [a.hash.slice(1), a]));
    const parts = [...links.keys()].map((id) => document.getElementById(id)).filter(Boolean);
    const seen = new Set();
    const mark = () => {
      const current = parts.find((p) => seen.has(p)) ?? null;
      for (const [id, a] of links) {
        if (current && id === current.id) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      }
    };
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) e.isIntersecting ? seen.add(e.target) : seen.delete(e.target);
        mark();
      },
      { rootMargin: '-96px 0px -55% 0px' }
    );
    for (const p of parts) this.observer.observe(p);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}
if (!customElements.get('am-scroll-spy')) customElements.define('am-scroll-spy', AmScrollSpy);
